import { Role, Permission } from '../types'
import { RoleManagement } from '@/views/roles/data/schema'
import { get_role, update_role, create_role, delete_role, get_roles, get_roles_list } from '@/api/roles'
import { get_permissions } from '@/api/permissions'

// Helper to map backend Role to frontend Role
const mapRole = (backendRole: any): Role => {
  return {
    id: backendRole.id.toString(),
    name: backendRole.name, // Ensure name is mapped correctly
    description: backendRole.description || '',
    updatesAllowed: !backendRole.is_system_role, // Assuming system roles are locked
    isEnabled: backendRole.is_enabled !== undefined ? backendRole.is_enabled : true,
    permissions: backendRole.permissions?.map((p: any) => p.full_codename || `${p.app_label}.${p.codename}`) || [],
  }
}

// Helper to resolve permission string to ID
// This is inefficient but necessary if we only have string on frontend.
// Optimization: Cache permissions or change frontend to use objects.
const getPermissionId = async (permissionString: string): Promise<number | undefined> => {
  const allPerms = await get_permissions();
  const perm = allPerms.find((p: any) => 
    (p.full_codename === permissionString) || 
    (`${p.app_label}.${p.codename}` === permissionString)
  );
  return perm?.id;
}

export const roleApi = {
  getAllRoles: async (): Promise<Role[]> => {
    const roles = await get_roles();
    return roles.map(mapRole);
  },

  getRolesList: async (): Promise<Role[]> => {
    const roles = await get_roles_list();
    return roles.map(mapRole);
  },

  getRoleById: async (id: string): Promise<Role> => {
    const role = await get_role(parseInt(id));
    return mapRole(role);
  },

  attachPermission: async (
    roleId: string,
    permission: string
  ): Promise<void> => {
    // 1. Get Permission ID from permission string
    const permId = await getPermissionId(permission);
    if (!permId) {
        console.error(`Permission ${permission} not found`);
        throw new Error(`Permission ${permission} not found`);
    }

    // 2. Use dedicated endpoint to attach permission
    const { attach_permission_to_role } = await import('@/api/roles');
    await attach_permission_to_role(parseInt(roleId), permId);
  },

  detachPermission: async (
    roleId: string,
    permission: string
  ): Promise<void> => {
    // 1. Get Permission ID from permission string
    const permId = await getPermissionId(permission);
    if (!permId) {
        console.error(`Permission ${permission} not found`);
        throw new Error(`Permission ${permission} not found`);
    }

    // 2. Use dedicated endpoint to detach permission
    const { detach_permission_from_role } = await import('@/api/roles');
    await detach_permission_from_role(parseInt(roleId), permId);
  },

  createRole: async (data: {
    name: string
    description?: string
    permissions?: string[]
  }): Promise<Role> => {
    // We need to convert permission strings to IDs for creation
    let permissionIds: number[] = [];
    if (data.permissions && data.permissions.length > 0) {
        const allPerms = await get_permissions();
        permissionIds = data.permissions.map(pString => {
            const p = allPerms.find((ap: any) => (ap.full_codename === pString) || (`${ap.app_label}.${ap.codename}` === pString));
            return p?.id;
        }).filter((id): id is number => id !== undefined);
    }

    const payload = {
        name: data.name,
        description: data.description,
        permission_ids: permissionIds
    };

    const newRole = await create_role(payload as unknown as Partial<RoleManagement>);
    return mapRole(newRole);
  },

  updateRole: async (
    id: string,
    data: {
      name?: string
      description?: string
      isEnabled?: boolean
    }
  ): Promise<Role> => {
    const payload: any = { ...data };
    if (data.isEnabled !== undefined) {
      payload.is_active = data.isEnabled;
      delete payload.isEnabled;
    }
    const updated = await update_role(parseInt(id), payload as unknown as Partial<RoleManagement>);
    return mapRole(updated);
  },

  deleteRole: async (id: string): Promise<void> => {
    await delete_role(parseInt(id))
  },
}

export const permissionApi = {
  getAllPermissions: async (): Promise<Permission[]> => {
     // Backend returns permissions in the correct format:
     // { id, name, codename, app_label, model, full_codename }
     return await get_permissions();
  },
}
